import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import VenueBookingStatistics from './VenueBookingStatistics';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

interface Venue {
  id: string;
  name: string;
  pricePerHour: number;
}

const Statistics: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, isVenueOperator, user } = useAuth();
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);

  // Mock data - később API-ból jön
  const stats = {
    totalGames: 1247,
    totalPlayers: 856,
    averageGameDuration: 18.5,
    peakHours: '19:00 - 22:00',
    popularGameMode: '501',
    totalRevenue: 45231,
    activeVenues: 12,
  };

  // User-specific statistics - később Firestore-ból jön a bejelentkezett felhasználó adataival
  const userStats = {
    totalGames: 24,
    wins: 15,
    losses: 7,
    draws: 2,
    winRate: 62.5,
    averageScore: 45.8,
    highestCheckout: 170,
    total180s: 3,
    weeklyGames: 3,
    scoreImprovement: 2.3,
  };

  const gameModesData = [
    { mode: '501', games: 567, percentage: 45.5 },
    { mode: '301', games: 423, percentage: 33.9 },
    { mode: 'Cricket', games: 257, percentage: 20.6 },
  ];

  const recentActivity = [
    { id: 1, player: 'Nagy Péter', game: '501', score: 180, time: '2 órája', result: 'win', opponent: 'Kiss Anna', finalScore: '3-1', venue: 'Kávézó A', duration: '25 perc' },
    { id: 2, player: 'Kiss Anna', game: 'Cricket', score: 156, time: '3 órája', result: 'loss', opponent: 'Szabó János', finalScore: '1-3', venue: 'Pub B', duration: '30 perc' },
    { id: 3, player: 'Szabó János', game: '301', score: 140, time: '5 órája', result: 'win', opponent: 'Tóth Eszter', finalScore: '3-0', venue: 'Étterem C', duration: '18 perc' },
    { id: 4, player: 'Tóth Eszter', game: '501', score: 167, time: '6 órája', result: 'loss', opponent: 'Kovács Márk', finalScore: '2-3', venue: 'Kávézó A', duration: '35 perc' },
    { id: 5, player: 'Kovács Márk', game: 'Cricket', score: 134, time: '8 órája', result: 'draw', opponent: 'Nagy Péter', finalScore: '2-2', venue: 'Pub B', duration: '28 perc' },
  ];

  useEffect(() => {
    if (isVenueOperator && user?.uid) {
      loadVenues();
    }
  }, [isVenueOperator, user]);

  const loadVenues = async () => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      // Get operator's venues
      const venuesQuery = query(
        collection(db, 'venues'),
        where('operatorId', '==', user.uid)
      );

      const venuesSnapshot = await getDocs(venuesQuery);
      const venuesData: Venue[] = [];

      venuesSnapshot.forEach((doc) => {
        const data = doc.data();
        venuesData.push({
          id: doc.id,
          name: data.name,
          pricePerHour: data.pricePerHour || 2500
        });
      });

      setVenues(venuesData);
    } catch (error) {
      console.error('Error loading venues:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gradient-cyan mb-2">
            📊 Statisztikák
          </h1>
          <p className="text-[#B8B8C3]">
            Részletes elemzések és teljesítmény mutatók
          </p>
        </div>

        {/* Time Range Selector - Only for Admins and Regular Users */}
        {!isVenueOperator && (
          <div className="flex gap-2">
            <button
              onClick={() => setTimeRange('week')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${timeRange === 'week'
                ? 'bg-[#00AFF2] text-white'
                : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                }`}
            >
              Hét
            </button>
            <button
              onClick={() => setTimeRange('month')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${timeRange === 'month'
                ? 'bg-[#00AFF2] text-white'
                : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                }`}
            >
              Hónap
            </button>
            <button
              onClick={() => setTimeRange('year')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${timeRange === 'year'
                ? 'bg-[#00AFF2] text-white'
                : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                }`}
            >
              Év
            </button>
          </div>
        )}
      </div>

      {/* Venue Booking Statistics - For Venue Operators */}
      {isVenueOperator && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
            </div>
          ) : venues.length > 0 ? (
            <VenueBookingStatistics venues={venues} />
          ) : (
            <div className="card-dark p-6 text-center">
              <p className="text-[#B8B8C3]">Nincs hozzárendelt helyszín.</p>
            </div>
          )}
        </>
      )}

      {/* Key Metrics - Only for Admins */}
      {isAdmin && !isVenueOperator && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#00AFF2]"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#00AFF2]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Összes Játék</p>
            <p className="text-3xl font-bold text-white">{stats.totalGames.toLocaleString()}</p>
            <p className="text-sm text-green-400 mt-2">↗ +12% az előző hónaphoz képest</p>
          </div>

          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#2bc48a]"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#2bc48a]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#2bc48a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Aktív Játékosok</p>
            <p className="text-3xl font-bold text-white">{stats.totalPlayers.toLocaleString()}</p>
            <p className="text-sm text-green-400 mt-2">↗ +8% az előző hónaphoz képest</p>
          </div>

          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#d9a514]"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#d9a514]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#d9a514]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Átlag Játékidő</p>
            <p className="text-3xl font-bold text-white">{stats.averageGameDuration} perc</p>
            <p className="text-sm text-[#B8B8C3] mt-2">Játékonként</p>
          </div>

          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-purple-400"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Aktív Helyszínek</p>
            <p className="text-3xl font-bold text-white">{stats.activeVenues}</p>
            <p className="text-sm text-purple-400 mt-2">Működő venue-k</p>
          </div>
        </div>
      )}

      {/* Personal Analytics - For Regular Users */}
      {!isAdmin && !isVenueOperator && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#00AFF2]"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#00AFF2]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Játékaid</p>
            <p className="text-3xl font-bold text-white">{userStats.totalGames}</p>
            <p className="text-sm text-green-400 mt-2">↗ +{userStats.weeklyGames} ezen a héten</p>
          </div>

          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#2bc48a]"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#2bc48a]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#2bc48a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Győzelmeid</p>
            <p className="text-3xl font-bold text-white">{userStats.wins}</p>
            <p className="text-sm text-[#2bc48a] mt-2">{userStats.winRate}% győzelmi arány</p>
          </div>

          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#d9a514]"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#d9a514]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#d9a514]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Átlag Pontszám</p>
            <p className="text-3xl font-bold text-white">{userStats.averageScore}</p>
            <p className="text-sm text-green-400 mt-2">↗ +{userStats.scoreImprovement} javulás</p>
          </div>

          <div className="relative card-dark p-6 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#ff6b6b]"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#ff6b6b]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#ff6b6b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
            </div>
            <p className="text-[#B8B8C3] text-sm mb-1">Legjobb Checkout</p>
            <p className="text-3xl font-bold text-white">{userStats.highestCheckout}</p>
            <p className="text-sm text-[#B8B8C3] mt-2">Személyes rekord</p>
          </div>
        </div>
      )}

      {/* Game Modes Distribution - Only for Admins and Regular Users */}
      {!isVenueOperator && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-dark p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Játékmódok Eloszlása</h2>
            <div className="space-y-4">
              {gameModesData.map((mode) => (
                <div key={mode.mode}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">{mode.mode}</span>
                    <span className="text-[#B8B8C3]">{mode.games} játék ({mode.percentage}%)</span>
                  </div>
                  <div className="w-full bg-[#15191E] rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] h-3 rounded-full transition-all duration-500"
                      style={{ width: `${mode.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Peak Hours */}
          <div className="card-dark p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Csúcsidőszakok</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Legforgalmasabb Időszak</p>
                  <p className="text-[#B8B8C3] text-sm">Legtöbb játék ebben az időszakban</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#00AFF2]">{stats.peakHours}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Legnépszerűbb Játékmód</p>
                  <p className="text-[#B8B8C3] text-sm">Legtöbbet játszott mód</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#d9a514]">{stats.popularGameMode}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Aktív Helyszínek</p>
                  <p className="text-[#B8B8C3] text-sm">Működő venue-k száma</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#2bc48a]">{stats.activeVenues}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Matches - Only for Admins and Regular Users */}
      {!isVenueOperator && (
        <div className="card-dark p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Legutóbbi Mérkőzések</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#31383D]">
                  <th className="text-left py-3 px-4 text-[#B8B8C3] font-medium">Játékos</th>
                  <th className="text-left py-3 px-4 text-[#B8B8C3] font-medium">Játékmód</th>
                  <th className="text-left py-3 px-4 text-[#B8B8C3] font-medium">Eredmény</th>
                  <th className="text-left py-3 px-4 text-[#B8B8C3] font-medium">Időpont</th>
                  <th className="text-left py-3 px-4 text-[#B8B8C3] font-medium">Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.map((activity) => (
                  <tr key={activity.id} className="border-b border-[#31383D] hover:bg-[#15191E] transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] flex items-center justify-center text-white font-bold">
                          {activity.player.charAt(0)}
                        </div>
                        <span className="text-white font-medium">{activity.player} - {activity.opponent}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="px-3 py-1 bg-[#00AFF2]/10 text-[#00AFF2] rounded-full text-sm font-medium">
                        {activity.game}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${activity.result === 'win' ? 'bg-green-500/20 text-green-400' :
                        activity.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                        {activity.result === 'win' && t('common.won')}
                        {activity.result === 'loss' && t('common.lost')}
                        {activity.result === 'draw' && t('common.draw')}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-[#B8B8C3]">{activity.time}</td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => {
                          setSelectedMatch(activity);
                          setShowMatchModal(true);
                        }}
                        className="text-[#00AFF2] hover:text-[#2bc48a] transition-colors"
                        title="Részletek megtekintése"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Match Details Modal */}
      {showMatchModal && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={() => setShowMatchModal(false)}>
          <div className="card-dark p-8 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gradient-cyan">Mérkőzés Részletei</h3>
              <button
                onClick={() => setShowMatchModal(false)}
                className="text-[#8a8f99] hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-[#1d232a] rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] flex items-center justify-center text-white font-bold text-2xl mb-3">
                      {selectedMatch.player.charAt(0)}
                    </div>
                    <p className="text-white font-bold text-lg">{selectedMatch.player}</p>
                    <p className="text-[#B8B8C3] text-sm mt-1">{selectedMatch.score} pont</p>
                  </div>

                  <div className="px-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-white mb-2">{selectedMatch.finalScore}</p>
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedMatch.result === 'win' ? 'bg-green-500/20 text-green-400' :
                          selectedMatch.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                        }`}>
                        {selectedMatch.result === 'win' && t('common.won')}
                        {selectedMatch.result === 'loss' && t('common.lost')}
                        {selectedMatch.result === 'draw' && t('common.draw')}
                      </span>
                    </div>
                  </div>

                  <div className="text-center flex-1">
                    <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-[#d9a514] to-[#ffb800] flex items-center justify-center text-white font-bold text-2xl mb-3">
                      {selectedMatch.opponent.charAt(0)}
                    </div>
                    <p className="text-white font-bold text-lg">{selectedMatch.opponent}</p>
                    <p className="text-[#B8B8C3] text-sm mt-1">Ellenfél</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#1d232a] rounded-lg p-4">
                  <p className="text-[#B8B8C3] text-sm mb-1">Játékmód</p>
                  <p className="text-white font-bold text-lg">{selectedMatch.game}</p>
                </div>
                <div className="bg-[#1d232a] rounded-lg p-4">
                  <p className="text-[#B8B8C3] text-sm mb-1">Helyszín</p>
                  <p className="text-white font-bold text-lg">{selectedMatch.venue}</p>
                </div>
                <div className="bg-[#1d232a] rounded-lg p-4">
                  <p className="text-[#B8B8C3] text-sm mb-1">Időtartam</p>
                  <p className="text-white font-bold text-lg">{selectedMatch.duration}</p>
                </div>
                <div className="bg-[#1d232a] rounded-lg p-4">
                  <p className="text-[#B8B8C3] text-sm mb-1">Időpont</p>
                  <p className="text-white font-bold text-lg">{selectedMatch.time}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowMatchModal(false)}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;
